// Buscas da tela da Cobrança (#6502). A regra de cada número está em
// src/lib/visaoGeral/cobranca.ts.
import { useEffect, useState } from 'react';
import { supabase } from '../../src/lib/supabase';
import { listarPessoas } from '../../src/services/indicacoes';
import {
  recuperacaoDoPeriodo,
  type AcaoDaCarteira, type ClienteDaCarteira, type ContatoDeCobranca, type FaturaPaga, type Recuperacao,
} from '../../src/lib/visaoGeral/cobranca';

const PAGINA = 1000;

async function paginar<T>(montar: () => any): Promise<T[]> {
  const todas: T[] = [];
  for (let p = 0; ; p++) {
    const { data, error } = await montar().range(p * PAGINA, p * PAGINA + PAGINA - 1);
    if (error) throw error;
    todas.push(...((data || []) as T[]));
    if (!data || data.length < PAGINA) break;
  }
  return todas;
}

function useBusca<T>(buscar: () => Promise<T>, deps: unknown[], rotulo: string): { dados: T | null; erro: boolean } {
  const [estado, setEstado] = useState<{ dados: T | null; erro: boolean }>({ dados: null, erro: false });
  useEffect(() => {
    let vivo = true;
    setEstado({ dados: null, erro: false });
    buscar()
      .then(dados => { if (vivo) setEstado({ dados, erro: false }); })
      .catch(e => { console.error(`${rotulo}:`, e); if (vivo) setEstado({ dados: null, erro: true }); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
  return estado;
}

/** Ações pendentes da régua com a pessoa como responsável. */
export const useReguaDaCarteira = (pessoaId: string) => useBusca<AcaoDaCarteira[]>(() =>
  paginar<AcaoDaCarteira>(() => supabase.from('cobranca_acao_manual')
    .select('id, etapa, data_referencia, status, responsavel_id, executado_por, executado_em, cliente_nome, valor_total')
    .eq('responsavel_id', pessoaId)
    .eq('status', 'pendente')
    .order('id')), [pessoaId], 'Régua da carteira');

/** Clientes da carteira da pessoa marcados como "negociado" (as promessas). */
export const usePromessasDaCarteira = (pessoaId: string) => useBusca<ClienteDaCarteira[]>(async () => {
  const { data, error } = await supabase.from('cobranca_clientes')
    .select('id, cliente_nome, cpf_cnpj_cliente, responsavel_cobranca_id, status_negociacao, valor_negociado, data_pagamento_negociado')
    .eq('responsavel_cobranca_id', pessoaId)
    .eq('status_negociacao', 'negociado');
  if (error) throw error;
  return (data || []) as ClienteDaCarteira[];
}, [pessoaId], 'Promessas');

/**
 * Recuperado por pessoa nos boletos pagos entre `inicio` e `fim`: as faturas pagas com
 * atraso e os contatos registrados (histórico da carteira e régua concluída) desde o
 * vencimento mais antigo entre elas.
 */
export const useRecuperacaoDoPeriodo = (inicio: Date, fim: Date) => {
  const de = inicio.toISOString();
  const ate = fim.toISOString();
  return useBusca<{ porPessoa: Map<string, Recuperacao>; semContato: Recuperacao }>(async () => {
    const faturas = await paginar<FaturaPaga>(() => supabase.from('faturas')
      .select('id, cpf_cnpj_cliente_boleto, cliente_cpf_cnpj, vencimento_boleto, data_pgto_boleto, valor_pagamento, valor_boleto_vigor')
      .eq('status_boleto_vigor', 'Pago')
      .gte('data_pgto_boleto', de)
      .lt('data_pgto_boleto', ate)
      .order('id'));
    const vencimentos = faturas.map(f => f.vencimento_boleto).filter((v): v is string => !!v).sort();
    if (vencimentos.length === 0) return recuperacaoDoPeriodo([], []);
    const desde = `${vencimentos[0].slice(0, 10)}T00:00:00`;
    const [clientes, historico, regua] = await Promise.all([
      paginar<{ id: string; cpf_cnpj_cliente: string | null }>(() => supabase.from('cobranca_clientes').select('id, cpf_cnpj_cliente').order('id')),
      paginar<{ cobranca_cliente_id: string; user_id: string | null; contato_em: string | null; created_at: string }>(() =>
        supabase.from('cobranca_negociacao_historico')
          .select('id, cobranca_cliente_id, user_id, contato_em, created_at')
          .eq('tipo_evento', 'contato')
          .gte('created_at', new Date(desde).toISOString())
          .order('id')),
      paginar<{ cpf_cnpj_cliente: string | null; executado_por: string | null; executado_em: string | null }>(() =>
        supabase.from('cobranca_acao_manual')
          .select('id, cpf_cnpj_cliente, executado_por, executado_em')
          .eq('status', 'concluido')
          .gte('executado_em', new Date(desde).toISOString())
          .order('id')),
    ]);
    const docDoCliente = new Map(clientes.map(c => [String(c.id), c.cpf_cnpj_cliente]));
    const contatos: ContatoDeCobranca[] = [
      ...historico.map(h => ({ doc: docDoCliente.get(String(h.cobranca_cliente_id)) ?? null, por: h.user_id, em: h.contato_em ?? h.created_at })),
      ...regua.filter(a => a.executado_em).map(a => ({ doc: a.cpf_cnpj_cliente, por: a.executado_por, em: a.executado_em! })),
    ];
    return recuperacaoDoPeriodo(faturas, contatos);
  }, [de, ate], 'Recuperação do mês');
};

/**
 * Clientes para abordar no Programa de Indicações: quem pediu para voltar e quem nunca
 * foi abordado. Só para quem trabalha o programa (sem a permissão o banco devolve só o
 * que a pessoa registrou).
 */
export const useIndicacoesParaTrabalhar = (pode: boolean) => useBusca<{ voltarDepois: number; nuncaAbordados: number } | null>(async () => {
  if (!pode) return null;
  const [voltar, nunca] = await Promise.all([
    listarPessoas({ filtro: 'voltar_depois', limite: 1 }),
    listarPessoas({ filtro: 'nunca_abordados', limite: 1 }),
  ]);
  return { voltarDepois: Number(voltar[0]?.total ?? 0), nuncaAbordados: Number(nunca[0]?.total ?? 0) };
}, [pode], 'Indicações para trabalhar');
